import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { ApiService } from "src/app/shared/services/api.service";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root"
})
export class VehicleTrackingService {
  constructor(
    private apiService: ApiService,
    private http: HttpClient
  ) {}

  /** Live GPS — fetch active drivers with current locations (optional geo-fence filter). */
  listActiveDrivers(parameters: any): Observable<any> {
    return this.apiService.get({ url: "/api/dispatcher/vehicle_tracking/drivers", parameters });
  }

  /**
   * Cycle-by-cycle reconstruction of a booking's driver search.
   *
   * Takes the booking number an operator can read off the ride, not a Mongo id — the tracking screen has
   * no booking id to hand. The endpoint checks the live collection then history, so one call covers a
   * booking whichever side of the split it sits on.
   */
  getBookingDispatchReplay(bookingNumber: string): Observable<any> {
    return this.apiService.get({ url: `/api/booking/dispatch_replay/${bookingNumber}`, parameters: { isShowSuccessToast: false } });
  }

  /** Historical playback — chronological route points for a driver. */
  getDriverHistory(driverId: string, parameters: any): Observable<any> {
    return this.apiService.get({ url: `/api/dispatcher/vehicle_tracking/driver_history/${driverId}`, parameters });
  }

  /**
   * Area + time-window search — which vehicles were inside the drawn shape.
   * POST because the polygon ring is too large for a query string.
   */
  areaSearch(parameters: { polygon: [number, number][]; from: number; to: number; businessType?: number | null }): Observable<any> {
    return this.apiService.post({ url: "/api/dispatcher/vehicle_tracking/area_search", parameters });
  }

  /**
   * Look up a trip by its human-facing trip number. Returns the route, the
   * driver, where the driver was when the trip was assigned, and a suggested
   * playback window.
   */
  getTripByNumber(uniqueId: number | string): Observable<any> {
    return this.apiService.get({ url: `/api/dispatcher/vehicle_tracking/trip/${uniqueId}`, parameters: {} });
  }

  /**
   * Download the CSV tracking report. Uses HttpClient (so the auth interceptor adds headers),
   * then triggers a browser download from the blob.
   */
  downloadExport(parameters: { driverId?: string; from: number; to: number }): Observable<Blob> {
    const params: any = {};
    Object.entries(parameters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params[k] = String(v);
    });

    return this.http
      .get(`${environment.API_URL}/api/dispatcher/vehicle_tracking/export`, {
        params,
        responseType: "blob"
      })
      .pipe(
        tap((blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `tracking_report_${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
      );
  }
}
